import { sha256 } from "js-sha256";
import * as Sentry from "@sentry/vue";


// Local to www: v4 @umi/shared/errors no longer exports this class.
export class NullEncryptionKeyError extends Error {
  constructor() {
    super("Encryption key is null");
    this.name = "NullEncryptionKeyError";
  }
}



const textEncoder = new TextEncoder();
const textDecoder = new TextDecoder();

const base64toBuffer = (base64: string) => Uint8Array.from(atob(base64), c => c.charCodeAt(0));
const bufferToBase64 = (buffer: Uint8Array) => btoa(new Uint8Array(buffer).reduce((data, byte) => data + String.fromCharCode(byte), ""));


let aesKeyCache: CryptoKey | null = null;


async function getAesKey(keyString: string) {

  if (aesKeyCache) return aesKeyCache;

  // Normalize the key to 32 bytes for AES-256
  const hashBuffer = sha256.arrayBuffer(keyString);
  const aesKeyData = new Uint8Array(hashBuffer).slice(0, 32);
  aesKeyCache = await crypto.subtle.importKey(
    "raw",
    aesKeyData,
    { name: "AES-GCM" },
    false,
    ["encrypt", "decrypt"]
  );
  return aesKeyCache;
}


/**
 * Encrypt a payload using the encryption key of the authenticated user.
 * @param clearPayload
 * @returns
 */
export const encrypt = async (clearPayload: string) => {

  const authStore = useAuthStore();
  const encryptionKey = authStore.encryptionKey;
  if (!encryptionKey) throw new NullEncryptionKeyError();


  const iv       = crypto.getRandomValues(new Uint8Array(12));
  const aesKey   = await getAesKey(encryptionKey);
  const ciphered = await crypto.subtle.encrypt(
    { name: "AES-GCM", iv },
    aesKey,
    textEncoder.encode(clearPayload)
  );

  const cipheredArr = new Uint8Array(ciphered);
  const buffer      = new Uint8Array(iv.byteLength + cipheredArr.byteLength);
  buffer.set(iv, 0);
  buffer.set(cipheredArr, iv.byteLength);

  return bufferToBase64(buffer);
};


/**
 * Decrypt a payload using the encryption key of the authenticated user.
 * @param cipherPayload
 * @param tag
 * @returns
 */
export const decrypt = async (cipherPayload: string, tag: string) => {

  try {

    const authStore = useAuthStore();
    const encryptionKey = authStore.encryptionKey;
    if (!encryptionKey) throw new NullEncryptionKeyError();

    if (!cipherPayload) throw new Error(`Decrypt payload for tag: ${tag} is empty`);


    const buffer = base64toBuffer(cipherPayload);
    const iv     = buffer.slice(0, 12);
    const data   = buffer.slice(12);
    const key    = await getAesKey(encryptionKey);

    const deciphered = await crypto.subtle.decrypt(
      { name: "AES-GCM", iv },
      key,
      data
    );

    return textDecoder.decode(deciphered);

  } catch (err) {
    const authStore = useAuthStore();
    Sentry.captureException(err, { extra: {
      tag,
      userId: authStore.user?.id,
      message: `Could not decrypt object with tag: ${tag}`
    } });
    throw err;
  }

};
